/**
 * Host-to-surface mapping with no Workers or Hono dependencies, so browser code (apps/portal) can import it
 * via `@qm/tenancy/surface` without pulling the D1/R2/KV/Queue types of the rest of this package.
 */
/** Which host surface the request came from. */
export type Surface = 'customer' | 'partner' | 'admin' | 'public' | 'internal';

export const HOST_SURFACES: Record<string, Surface> = {
  'portal.quixomarketing.com': 'customer',
  'partners.quixomarketing.com': 'partner',
  'admin.quixomarketing.com': 'admin',
  'quixomarketing.com': 'public',
  'www.quixomarketing.com': 'public',
};

/** Map a host to its surface. dev/stg hosts use a prefix (portal-dev., partners-stg., ...). */
export function surfaceFromHost(host: string | null | undefined): Surface {
  if (!host) return 'public';
  const h = host.toLowerCase().split(':')[0]!;
  if (HOST_SURFACES[h]) return HOST_SURFACES[h]!;
  const first = h.split('.')[0] ?? '';
  if (first.startsWith('portal')) return 'customer';
  if (first.startsWith('partners')) return 'partner';
  if (first.startsWith('admin')) return 'admin';
  return 'public';
}

const SURFACE_HOST_PREFIX: Record<'customer' | 'partner' | 'admin', string> = {
  customer: 'portal', partner: 'partners', admin: 'admin',
};

/**
 * The origin of `target` surface that belongs to the same environment as `host`: partners.x -> portal.x
 * (target customer), admin-stg.x -> partners-stg.x (target partner), portal-dev.x -> admin-dev.x (target
 * admin), and so on for every surface pair. Unknown hosts (localhost, previews) stay on the same origin, so
 * dev and stg never link to production.
 */
export function originForSurface(host: string, target: 'customer' | 'partner' | 'admin', protocol = 'https:'): string {
  const h = host.toLowerCase();
  const [first = '', ...rest] = h.split('.');
  const m = /^(portal|partners|admin)(-[a-z0-9]+)?$/.exec(first);
  if (!m || rest.length === 0) return `${protocol}//${host}`;
  return `${protocol}//${SURFACE_HOST_PREFIX[target]}${m[2] ?? ''}.${rest.join('.')}`;
}

/** The customer portal origin for the same environment as `host` (QM-C04-002 partner sign-in link). */
export function customerOriginFor(host: string, protocol = 'https:'): string {
  return originForSurface(host, 'customer', protocol);
}

/** The partner portal origin for the same environment as `host`. */
export function partnerOriginFor(host: string, protocol = 'https:'): string {
  return originForSurface(host, 'partner', protocol);
}

/** The staff admin console origin for the same environment as `host` (owner request 27 Sep 2026: cross-link a
 * staff-domain email typed into the customer or partner sign-in to admin.quixomarketing.com). */
export function adminOriginFor(host: string, protocol = 'https:'): string {
  return originForSurface(host, 'admin', protocol);
}
